import React from 'react';
import {
  Box, Checkbox, Collapse, FormControlLabel, IconButton, Link, Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { BORDER } from '../common/tokens';

const checkboxSx = (theme) => ({
  color: BORDER,
  '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: theme.palette.primary.main },
});

/**
 * The folders found in a bookmark file, each of which becomes a list. A folder
 * ticks whole or in part; expanding one shows its links so individual ones can
 * be dropped.
 */
export default function FolderTree({
  folders, expanded, disabled, totalSelected, maxUrls, overLimit,
  onToggleFolder, onToggleUrl, onToggleExpand,
}) {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {`${folders.length} folder${folders.length !== 1 ? 's' : ''} found — each will become a list`}
      </Typography>
      <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
        {folders.map((folder, fi) => {
          const isExpanded = expanded.has(fi);
          const selectedCount = folder.selectedUrls.filter(Boolean).length;
          const allSelected = selectedCount === folder.urls.length;
          const someSelected = selectedCount > 0 && !allSelected;
          return (
            <Box key={folder.name}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  disabled={disabled}
                  onChange={(e) => onToggleFolder(fi, e.target.checked)}
                  size="small"
                  sx={checkboxSx}
                />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {folder.name}
                  <Typography component="span" variant="body2" color="text.secondary">
                    {` · ${selectedCount}/${folder.urls.length} URL${folder.urls.length !== 1 ? 's' : ''}`}
                  </Typography>
                </Typography>
                <IconButton size="small" onClick={() => onToggleExpand(fi)}>
                  {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              </Box>
              <Collapse in={isExpanded}>
                <Box sx={{ pl: 4, pb: 1 }}>
                  {folder.urls.map((url, ui) => (
                    <FormControlLabel
                      key={url}
                      control={
                        <Checkbox
                          checked={folder.selectedUrls[ui]}
                          disabled={disabled}
                          onChange={(e) => onToggleUrl(fi, ui, e.target.checked)}
                          size="small"
                          sx={checkboxSx}
                        />
                      }
                      label={
                        <Typography variant="caption" noWrap display="block">
                          <Link href={url} target="_blank" rel="noopener noreferrer" underline="hover" color="text.secondary">
                            {url}
                          </Link>
                        </Typography>
                      }
                      sx={{ display: 'flex', mx: 0, my: 0 }}
                    />
                  ))}
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>
      <Typography variant="caption" color={overLimit ? 'error' : 'text.secondary'} sx={{ mt: 1, display: 'block' }}>
        {`${totalSelected}/${maxUrls} URLs selected`}
      </Typography>
    </Box>
  );
}
